import React from "react";
import { NavLink } from "react-router-dom";
import logo from "../assets/Img/airaa-logo-white.webp";
import certificate from "../assets/Pdf/IMC Membership-Certificate  26-27 ACT4031.pdf";
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { Clock, ArrowRight } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-blue-950 text-white overflow-hidden">
      {/* Animated blobs */}
      <div className="absolute top-0 left-1/4 w-72 h-72 rounded-full bg-emerald-500/8 blur-3xl animate-float pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-56 h-56 rounded-full bg-blue-400/8 blur-3xl animate-float-alt pointer-events-none" />

      {/* Wave top divider */}
      <div className="absolute top-0 left-0 right-0 overflow-hidden leading-none" style={{ height: 48 }}>
        <svg viewBox="0 0 1440 48" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,48 C360,0 1080,0 1440,48 L1440,0 L0,0 Z" fill="rgb(248 250 252)" />
        </svg>
      </div>

      <div className="relative pt-16 pb-10 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10">

          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src={logo} alt="AIRAA Logo" width="48" height="48" loading="lazy" className="object-contain w-12 h-12" />
              <div>
                <p className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider leading-tight">AIRAA</p>
                <p className="text-[9px] text-slate-400 leading-tight">All India Recruiting Agency Association</p>
              </div>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed mb-4">
              India's largest recruitment agency association, empowering ethical employment and global workforce mobility since 2011.
            </p>
            <NavLink
              to="/registration"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              Become a Member <ArrowRight size={12} />
            </NavLink>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-300 mb-5">Quick Links</h3>
            <ul className="space-y-2.5">
              {[
                { to: "/about-airaa", label: "About AIRAA" },
                { to: "/why-join-airaa", label: "Why Join AIRAA" },
                { to: "/member-services", label: "Member Services" },
                { to: "/helpline", label: "Helpline" },
                { to: "/gallery", label: "Gallery" },
                { to: "/newsletter", label: "Newsletter" },
                { to: "/socialresponsibility", label: "Initiatives" },
              ].map(({ to, label }) => (
                <li key={to}>
                  <NavLink to={to} className="text-slate-400 hover:text-emerald-300 text-xs transition-colors">
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-300 mb-5">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <FaMapMarkerAlt className="text-emerald-400 mt-0.5 shrink-0 text-xs" />
                <span className="text-slate-400 text-xs leading-relaxed">
                  Office No. 117 , Options Primo, 21st Rd, near passport Office, Shree Krishna Nagar, Marol MIDC Industry Estate, Andheri East, Mumbai, Maharashtra 400093
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <FaPhone className="text-emerald-400 shrink-0 text-xs" />
                <a href="tel:+917738355865" className="text-slate-400 hover:text-emerald-300 text-xs transition-colors">
                  +91 7738355865
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <FaEnvelope className="text-emerald-400 shrink-0 text-xs" />
                <a href="mailto:airaa@airaa.co.in" className="text-slate-400 hover:text-emerald-300 text-xs transition-colors">
                  airaa@airaa.co.in
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-300 mb-5">Working Hours</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2.5">
                <Clock size={12} className="text-emerald-400 shrink-0" />
                <span className="text-slate-400 text-xs">Mon – Sat: 10:30 AM – 7:00 PM</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Clock size={12} className="text-slate-600 shrink-0" />
                <span className="text-slate-500 text-xs">Sunday: Closed</span>
              </li>
            </ul>
            <a
              href={certificate}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              View IMC Certificate <ArrowRight size={12} />
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-500 text-xs">© {currentYear} AIRAA — All India Recruiting Agency Association. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <NavLink to="/contact-us" className="text-slate-500 hover:text-slate-300 text-xs transition-colors">Contact</NavLink>
            <NavLink to="/importantlinks" className="text-slate-500 hover:text-slate-300 text-xs transition-colors">Important Links</NavLink>
            {/* <NavLink to="/notices" className="text-slate-500 hover:text-slate-300 text-xs transition-colors">Notices</NavLink> */}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
